import React, { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  MapPin, Camera, AlertTriangle, CheckCircle2, Loader2, Wrench, Plus, X,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { formatMoney, formatDateTime, formatAddress, toCents } from "@/lib/format";
import { woStatusTone } from "@/lib/wo";
import { StatusChip } from "@/components/common/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const client = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public/wo` });

const MAX_SIDE = 1600;

async function compress(file) {
  if (/heic|heif/i.test(file.type) || /\.heic$|\.heif$/i.test(file.name)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    return blob ? new File([blob], "foto.jpg", { type: "image/jpeg" }) : file;
  } catch (e) {
    return file;
  }
}

const BigButton = ({ children, onClick, tone = "dark", testId, disabled }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-[10px] px-4 text-[17px] font-semibold transition-colors disabled:opacity-50 ${
      tone === "dark"
        ? "bg-ink text-white active:bg-ink"
        : tone === "blue"
        ? "bg-brand text-white active:bg-brand-hover"
        : tone === "green"
        ? "bg-state-ok text-white active:bg-state-ok"
        : "border-2 border-line-strong bg-white text-ink-secondary active:bg-line"
    }`}
  >
    {children}
  </button>
);

export default function PublicWorkOrderPage() {
  const { token } = useParams();
  const { t } = useT();
  const [progress, setProgress] = useState(null);
  const [tipo, setTipo] = useState("antes");
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [extraOpen, setExtraOpen] = useState(false);
  const [extra, setExtra] = useState({ descripcion: "", monto: "", file: null });
  const fileRef = useRef(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["public-wo", token],
    retry: 1,
    queryFn: async () => (await client.get(`/${token}`)).data,
  });

  const abiertas = useMemo(
    () => (data?.observaciones || []).filter((o) => o.estado === "abierta"),
    [data]
  );

  const doTransition = async (destino, extraBody = {}) => {
    setBusy(true);
    try {
      await client.post(`/${token}/transition`, { destino, ...extraBody });
      toast.success(t("wo.public.sent"));
      refetch();
      setRejectOpen(false);
      setMotivo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("app.error"));
    } finally {
      setBusy(false);
    }
  };

  const uploadOne = async (rawFile, kind, attempt = 0) => {
    const file = await compress(rawFile);
    const form = new FormData();
    form.append("file", file);
    form.append("tipo", kind);
    try {
      setProgress(1);
      await client.post(`/${token}/evidence`, form, {
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      setProgress(null);
      toast.success(t("wo.public.sent"));
      refetch();
    } catch (e) {
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 1200));
        return uploadOne(rawFile, kind, attempt + 1);
      }
      setProgress(null);
      toast.error(e?.response?.data?.detail || t("app.error"));
    }
  };

  const sendExtra = async () => {
    if (!extra.file || extra.descripcion.trim().length < 5 || toCents(extra.monto) <= 0) {
      toast.error(t("app.required"));
      return;
    }
    setBusy(true);
    try {
      const file = await compress(extra.file);
      const form = new FormData();
      form.append("file", file);
      form.append("descripcion", extra.descripcion.trim());
      form.append("sub_cost_cents", String(toCents(extra.monto)));
      await client.post(`/${token}/additional`, form, {
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      setProgress(null);
      setExtraOpen(false);
      setExtra({ descripcion: "", monto: "", file: null });
      toast.success(t("wo.public.additional_sent"));
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("app.error"));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-white">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="public-wo-not-found"
        className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ink px-6 text-center text-white"
      >
        <AlertTriangle className="h-8 w-8 text-state-warn" />
        <p className="text-[17px]">{t("wo.public.not_found")}</p>
      </div>
    );
  }

  const { wo, lineas, evidencia, solo_lectura: readOnly, acciones } = data;
  const originales = lineas.filter((l) => l.tipo === "original");
  const has = (destino) => acciones.some((a) => a.destino === destino);

  return (
    <div className="min-h-screen bg-line pb-10" data-testid="public-wo-page">
      <header className="bg-ink px-5 pb-6 pt-6 text-white">
        <div className="mx-auto max-w-[560px]">
        <div className="flex items-center gap-2 text-[13px] text-ink-tertiary">
          <Wrench className="h-4 w-4" /> Beto's Handyman
        </div>
        <h1 className="mt-3 text-[30px] font-bold leading-none tabular-nums">{wo.code}</h1>
        <p className="mt-2 text-[19px] font-semibold">{wo.property_nombre}</p>
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(formatAddress(wo.property_direccion))}`}
          target="_blank"
          rel="noreferrer"
          data-testid="public-wo-map"
          className="mt-1.5 flex w-fit items-center gap-1.5 text-[16px] text-brand-hover underline"
        >
          <MapPin className="h-4 w-4" /> {formatAddress(wo.property_direccion)}
        </a>
        {wo.unit_etiqueta && (
          <p className="mt-3 w-fit rounded-[8px] bg-white/10 px-3 py-1.5 text-[18px] font-semibold">
            {wo.unit_etiqueta}
          </p>
        )}
        <div className="mt-4">
          <StatusChip tone={woStatusTone(wo.status)} label={t(`woStatus.${wo.status}`)} testId="public-wo-status" />
        </div>
        </div>
      </header>

      <main className="mx-auto max-w-[560px] space-y-4 px-4 pt-4">
        {readOnly && (
          <div
            data-testid="public-wo-readonly"
            className="rounded-[10px] border-2 border-line-strong bg-white px-4 py-3 text-[16px] text-ink-secondary"
          >
            {t("wo.public.closed")}
          </div>
        )}

        {!!abiertas.length && (
          <section
            data-testid="public-wo-observations"
            className="overflow-hidden rounded-[10px] border-2 border-state-critical-soft bg-state-critical-soft"
          >
            <h2 className="flex items-center gap-2 bg-state-critical-soft px-4 py-3 text-[17px] font-bold text-state-critical">
              <AlertTriangle className="h-5 w-5" /> {t("wo.public.observations")}
            </h2>
            <ul className="divide-y divide-state-critical-soft">
              {abiertas.map((o) => {
                const foto = evidencia.find((e) => e.id === o.evidence_id);
                return (
                  <li key={o.id} className="flex gap-3 px-4 py-3">
                    {foto && (
                      <img src={foto.thumb_url} alt="" className="h-20 w-20 shrink-0 rounded-[8px] object-cover" />
                    )}
                    <p className="text-[16px] leading-snug text-state-critical">{o.texto}</p>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="rounded-[10px] bg-white p-4">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
            {t("wo.public.scope")}
          </h2>
          <ul className="mt-2 space-y-2">
            {originales.map((l) => (
              <li key={l.id} className="flex gap-2.5 text-[17px] leading-snug text-ink">
                <span className="mt-[9px] h-2 w-2 shrink-0 rounded-full bg-brand" />
                {l.descripcion}
              </li>
            ))}
          </ul>
        </section>

        <section className="grid grid-cols-2 gap-3">
          <div className="rounded-[10px] bg-white p-4">
            <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
              {t("wo.public.amount")}
            </p>
            <p data-testid="public-wo-amount" className="mt-1 text-[26px] font-bold tabular-nums text-ink">
              {formatMoney(wo.sub_cost_total_cents ?? wo.sub_cost_cents)}
            </p>
          </div>
          <div className="rounded-[10px] bg-white p-4">
            <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
              {t("wo.public.due")}
            </p>
            <p className="mt-1 text-[17px] font-semibold text-ink">{formatDateTime(wo.due_at)}</p>
          </div>
        </section>

        {/* Fix L (2026-09-28): día y hora de la cita, grande, arriba de todo lo operativo. */}
        {wo.cita_texto && (
          <section data-testid="public-wo-appointment" className="rounded-[10px] border-2 border-state-ok bg-white p-4">
            <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
              {t("wo.public.appointment")}
            </p>
            <p className="mt-1 text-[20px] font-semibold text-ink">{wo.cita_texto}</p>
          </section>
        )}

        {wo.notas_acceso && (
          <section className="rounded-[10px] border-2 border-brand bg-brand-soft p-4">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.06em] text-brand-hover">
              {t("wo.public.access")}
            </h2>
            <p className="mt-1.5 text-[16px] leading-relaxed text-ink">{wo.notas_acceso}</p>
          </section>
        )}

        {!readOnly && (
          <section className="space-y-3">
            {has("aceptada") && (
              <BigButton tone="green" testId="public-wo-accept" disabled={busy}
                onClick={() => doTransition("aceptada")}>
                <CheckCircle2 className="h-5 w-5" /> {t("wo.public.accept")}
              </BigButton>
            )}
            {has("nueva") && (
              <BigButton tone="light" testId="public-wo-reject" disabled={busy}
                onClick={() => setRejectOpen(true)}>
                <X className="h-5 w-5" /> {t("wo.public.reject")}
              </BigButton>
            )}
            {/* Fix L: «Llegué» necesita al menos una foto «antes» (la guarda del backend ya lo
                exige); se avisa antes de tocar el botón para que no le salga un error. */}
            {has("en_curso") && !evidencia.some((e) => e.tipo === "antes") && (
              <p data-testid="public-wo-start-hint"
                className="rounded-[10px] border-2 border-state-warn bg-state-warn-soft px-4 py-3 text-[15px] leading-snug text-state-warn">
                {t("wo.public.start_hint")}
              </p>
            )}
            {has("en_curso") && (
              <BigButton tone="blue" testId="public-wo-start" disabled={busy}
                onClick={() => doTransition("en_curso")}>
                {t("wo.public.start")}
              </BigButton>
            )}

            <div className="rounded-[10px] bg-white p-4">
              <Label className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
                {t("wo.public.photo_type")}
              </Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["antes", "despues", "correccion"].map((x) => (
                  <button
                    key={x}
                    data-testid={`public-wo-phototype-${x}`}
                    onClick={() => setTipo(x)}
                    className={`min-h-[44px] rounded-[8px] text-[15px] font-semibold transition-colors ${
                      tipo === x ? "bg-ink text-white" : "border-2 border-line-strong bg-white text-ink-secondary"
                    }`}
                  >
                    {t(`wo.evidence.types.${x}`)}
                  </button>
                ))}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.heic,.heif"
                capture="environment"
                data-testid="public-wo-file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) uploadOne(f, tipo);
                }}
              />
              <div className="mt-3">
                <BigButton tone="dark" testId="public-wo-upload" disabled={progress !== null}
                  onClick={() => fileRef.current?.click()}>
                  {progress !== null ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> {t("wo.public.uploading")}
                    </>
                  ) : (
                    <>
                      <Camera className="h-5 w-5" /> {t("wo.public.upload")}
                    </>
                  )}
                </BigButton>
                {progress !== null && (
                  <Progress data-testid="public-wo-progress" value={progress} className="mt-2 h-2" />
                )}
              </div>
            </div>

            {has("en_revision") && (
              <BigButton tone="green" testId="public-wo-finish" disabled={busy}
                onClick={() => doTransition("en_revision")}>
                {wo.status === "en_correccion" ? t("wo.public.send_correction") : t("wo.public.finish")}
              </BigButton>
            )}

            <BigButton tone="light" testId="public-wo-additional" onClick={() => setExtraOpen(true)}>
              <Plus className="h-5 w-5" /> {t("wo.public.additional")}
            </BigButton>
          </section>
        )}

        {!!evidencia.length && (
          <section className="rounded-[10px] bg-white p-4">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink-tertiary">
              {t("wo.public.photos")}
            </h2>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {evidencia.map((e) => (
                <figure key={e.id} className="relative">
                  <img
                    src={e.thumb_url || e.url}
                    alt={e.tipo}
                    className="aspect-square w-full rounded-[8px] object-cover"
                  />
                  <figcaption className="absolute left-1 top-1 rounded-[4px] bg-ink/80 px-1.5 py-[2px] text-[10px] font-bold uppercase text-white">
                    {t(`wo.evidence.types.${e.tipo}`)}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}
      </main>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-[440px] rounded-[10px]">
          <DialogHeader>
            <DialogTitle className="text-[17px]">{t("wo.public.reject_motivo")}</DialogTitle>
          </DialogHeader>
          <Textarea
            data-testid="public-wo-reject-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="rounded-[8px] text-[16px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="h-11 rounded-[8px] text-[15px]">
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="public-wo-reject-send"
              disabled={busy || motivo.trim().length < 10}
              onClick={() => doTransition("nueva", { motivo })}
              className="h-11 rounded-[8px] bg-ink text-[15px]"
            >
              {t("wo.public.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent data-testid="public-wo-additional-dialog" className="max-w-[440px] rounded-[10px]">
          <DialogHeader>
            <DialogTitle className="text-[17px]">{t("wo.public.additional")}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-[14px]">{t("wo.public.additional_desc")}</Label>
            <Textarea
              data-testid="public-wo-additional-desc"
              value={extra.descripcion}
              onChange={(e) => setExtra({ ...extra, descripcion: e.target.value })}
              rows={3}
              className="mt-1 rounded-[8px] text-[16px]"
            />
          </div>
          <div>
            <Label className="text-[14px]">{t("wo.public.additional_amount")}</Label>
            <Input
              data-testid="public-wo-additional-amount"
              value={extra.monto}
              inputMode="decimal"
              onChange={(e) => setExtra({ ...extra, monto: e.target.value })}
              placeholder="0.00"
              className="mt-1 h-12 rounded-[8px] text-right text-[20px] font-semibold tabular-nums"
            />
          </div>
          <div>
            <Label className="text-[14px]">{t("wo.public.additional_photo")}</Label>
            <input
              type="file"
              accept="image/*,.heic,.heif"
              capture="environment"
              data-testid="public-wo-additional-file"
              onChange={(e) => setExtra({ ...extra, file: e.target.files?.[0] || null })}
              className="mt-1 block w-full text-[15px]"
            />
          </div>
          {progress !== null && <Progress value={progress} className="h-2" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraOpen(false)} className="h-11 rounded-[8px] text-[15px]">
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="public-wo-additional-send"
              disabled={busy}
              onClick={sendExtra}
              className="h-11 rounded-[8px] bg-brand text-[15px]"
            >
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {t("wo.public.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
